import moment from 'moment';

/**
 * Convert any time representation (24-hour clock string, ISO timestamp, or Date)
 * into standard 12-hour clock format with AM/PM (e.g. '02:30 PM', '09:00 AM', '12:00 PM', '12:00 AM').
 *
 * @param {string|Date|number} timeVal - Value to format
 * @returns {string} Formatted 12-hour time string
 */
export const formatTo12Hour = (timeVal) => {
  if (timeVal === null || timeVal === undefined || timeVal === '') return '';
  const str = String(timeVal).trim();
  if (!str || str.toLowerCase() === 'off' || str.toLowerCase() === 'select' || str === '-') {
    return str;
  }

  // Already 12-hour format with AM/PM (e.g. '02:30 PM', '2:30 pm', '1:40 PM')
  if (/^\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM|am|pm)$/i.test(str)) {
    const parts = str.split(/\s+/);
    const timeParts = parts[0].split(':');
    const meridiem = parts[1].toUpperCase();
    return `${timeParts[0].padStart(2, '0')}:${timeParts[1]} ${meridiem}`;
  }

  // 24-hour clock string e.g. '14:30', '07:00', '9:00', '13:40', '14:30:00'
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
    const m = moment(str, ['HH:mm', 'HH:mm:ss', 'H:mm']);
    if (m.isValid()) {
      return m.format('hh:mm A');
    }
  }

  // ISO timestamp or standard date-time string e.g. '2026-08-31T13:40:00.000Z', '2026-08-31 13:40:00'
  if (/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(str)) {
    const m = moment(str);
    if (m.isValid()) {
      return m.format('hh:mm A');
    }
  }

  // If timeVal is a JavaScript Date object
  if (timeVal instanceof Date && !Number.isNaN(timeVal.getTime())) {
    return moment(timeVal).format('hh:mm A');
  }

  // Pure integer / ID (e.g. '3', '14') or unparseable text
  return str;
};

/**
 * Format a full datetime into locale date and 12-hour time (e.g. '28-08-2026 02:30 PM').
 *
 * @param {string|Date} dateTime - Date/time to format
 * @param {string} dateFormat - Date format (default 'DD-MM-YYYY')
 * @returns {string} Formatted date and 12-hour time
 */
export const formatDateTime12Hour = (dateTime, dateFormat = 'DD-MM-YYYY') => {
  if (!dateTime) return '';
  const m = moment(dateTime);
  if (!m.isValid()) return '';
  return m.format(`${dateFormat} hh:mm A`);
};

// Table display formats are fixed across tenants while parsing remains local to
// the supplied value, preserving any timezone conversion already applied by callers.
export const formatTableDate = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const raw = String(value).trim();
  const datePart = raw.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  const parsed = value instanceof Date
    ? moment(value)
    : datePart
      ? moment(datePart, 'YYYY-MM-DD', true)
      : moment(value, [moment.ISO_8601, 'DD-MM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY'], true);
  return parsed.isValid() ? parsed.format('DD-MM-YY') : '—';
};

export const formatTableTime = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const formatted = formatTo12Hour(value);
  return formatted && formatted !== String(value).trim() ? formatted : '—';
};

export function formatTableDateTime(date, time, hasSeparateTime = false) {
  if ((time === null || time === undefined || time === '') && date) {
    const parsed = moment(date);
    if (parsed.isValid() && (date instanceof Date || /[T ]\d{2}:\d{2}/.test(String(date)))) {
      return parsed.format('DD-MM-YY, hh:mm A');
    }
  }
  if (hasSeparateTime && (!date || time === null || time === undefined || time === '')) return '—';
  const formattedDate = formatTableDate(date);
  const formattedTime = formatTableTime(time);
  if (formattedDate === '—') return '—';
  return formattedTime === '—' ? formattedDate : `${formattedDate}, ${formattedTime}`;
}

/**
 * Convert minutes from midnight to 12-hour format string (e.g. 510 -> '08:30 AM', 870 -> '02:30 PM').
 *
 * @param {number} minutes
 * @returns {string}
 */
export const minutesTo12HourClock = (minutes) => {
  const safe = Math.max(0, Math.round(minutes));
  const totalHours = Math.floor(safe / 60) % 24;
  const mins = safe % 60;
  const meridiem = totalHours >= 12 ? 'PM' : 'AM';
  const hour12 = totalHours % 12 === 0 ? 12 : totalHours % 12;
  return `${String(hour12).padStart(2, '0')}:${String(mins).padStart(2, '0')} ${meridiem}`;
};
